import { trackUserInteraction } from "@inrange/building-manager-api-client/events";
import { useEffect, useRef } from "react";
import { useParams } from "react-router-dom";

const SURVEY_BG = "#00022f";

const SURVEY_SUBMITTED_MESSAGE_TYPE = "INRANGE_SURVEY_SUBMITTED";
const SURVEY_NOT_FOUND_MESSAGE_TYPE = "INRANGE_SURVEY_NOT_FOUND";

interface SurveySubmittedPayload {
  surveyorCompanyName?: string;
  projectName?: string;
  projectReference?: string;
  overallSiteStatus?: string;
  submissionId?: string;
  surveyorName?: string;
  shortcode?: string;
}

const getApiUrl = (): string => {
  const branchName = import.meta.env?.VITE_BRANCH_NAME;
  if (import.meta.env?.PROD && branchName === "main") {
    return "https://bma-api.inrange.io";
  }
  const forced = import.meta.env?.VITE_FORCE_API_HOSTNAME;
  if (forced) return forced;
  return "https://bma-api-staging.inrange.io";
};

const SurveyPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Split on last dash: everything before is name, everything after is shortcode
  const lastDash = slug ? slug.lastIndexOf("-") : -1;
  const isValid = !!slug && lastDash > 0;
  const surveyorCompanyName = isValid ? slug.substring(0, lastDash) : "";
  const shortcode = isValid ? slug.substring(lastDash + 1) : "";
  const apiUrl = getApiUrl();

  const iframeSrc = isValid
    ? `/survey-app.html?apiUrl=${encodeURIComponent(apiUrl)}&name=${encodeURIComponent(surveyorCompanyName)}&shortcode=${encodeURIComponent(shortcode)}`
    : "";

  useEffect(() => {
    const prev = {
      background: document.body.style.background,
      overflow: document.body.style.overflow,
      position: document.documentElement.style.position,
      overflowHtml: document.documentElement.style.overflow,
    };
    document.body.style.background = SURVEY_BG;
    document.body.style.overflow = "hidden";
    document.documentElement.style.position = "fixed";
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.body.style.background = prev.background;
      document.body.style.overflow = prev.overflow;
      document.documentElement.style.position = prev.position;
      document.documentElement.style.overflow = prev.overflowHtml;
    };
  }, []);

  useEffect(() => {
    if (!isValid) return;

    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.source !== iframeRef.current?.contentWindow) return;

      // The iframe couldn't resolve this endpoint (deleted / bad link) and is
      // showing its friendly not-found page. Replace the dead link in the
      // address bar - which carries the endpoint's name + shortcode - with a
      // neutral, still-auth-free /survey path. Raw replaceState (not navigate)
      // leaves the iframe mounted showing the not-found page; only the bar
      // changes. Reloading /survey/not-found 404s the same way, so it stays
      // consistent.
      if (event.data?.type === SURVEY_NOT_FOUND_MESSAGE_TYPE) {
        window.history.replaceState({}, "", "/survey/not-found");
        return;
      }

      if (event.data?.type !== SURVEY_SUBMITTED_MESSAGE_TYPE) return;

      const payload = (event.data?.payload || {}) as SurveySubmittedPayload;
      void trackUserInteraction(
        {
          ...payload,
          shortcode,
          surveySlug: slug,
          surveyUrl: window.location.href,
        },
        "BASIC_ACTION",
        "SURVEY_SUBMITTED",
        undefined,
        "customer-app"
      );
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [isValid, surveyorCompanyName, shortcode, slug]);

  if (!isValid) {
    return (
      <div style={{ padding: 40, color: "white", background: SURVEY_BG }}>
        Invalid survey link.
      </div>
    );
  }

  return (
    <iframe
      ref={iframeRef}
      src={iframeSrc}
      style={{
        position: "fixed",
        inset: 0,
        width: "100%",
        height: "100%",
        border: "none",
        display: "block",
        background: SURVEY_BG,
      }}
      title="Site Survey"
    />
  );
};

export default SurveyPage;
